import React, { useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  Avatar,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Fade,
  Link,
  useTheme,
  useMediaQuery,
} from '@mui/material';

import { PeopleOutlined as People, TrendingUpOutlined as TrendingUp, SpeedOutlined as Speed, ErrorOutlined as Error, ArrowUpwardOutlined as ArrowUpward, ArrowDownwardOutlined as ArrowDownward, DeleteOutlined, GitHub as GitHubIcon } from '@mui/icons-material';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import { fetchAllUsers, fetchAnalytics, fetchAllFeedback, updateFeedbackStatus, deleteFeedback, FeedbackSortField, SortDir } from '../store/slices/adminSlice';
import ErrorBoundary from '../components/ErrorBoundary';
import FeedbackMonitor from '../components/admin/FeedbackMonitor';
import ChatMonitor from '../components/admin/ChatMonitor';
import Pagination from '../components/common/Pagination';
import FeedbackAttachmentThumb from '../components/common/FeedbackAttachmentThumb';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  BarChart as RechartsBarChart,
  Bar,
} from 'recharts';
import { safeFormatDate } from '../utils/dateUtils';
import backendUrl from '../store/config';

const SORTABLE_SERVER_FIELDS = [
  'created_at', 'rating', 'status', 'category', 'comment',
] as const satisfies ReadonlyArray<FeedbackSortField>;
type SortableServerField = typeof SORTABLE_SERVER_FIELDS[number];

const AdminDashboard: React.FC = () => {
  const dispatch = useAppDispatch();
  const { users, analytics, feedback, feedbackTotal, loading, error } = useAppSelector((state: any) => state.admin);
  const token = useAppSelector((state: any) => state.auth?.token);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [confirmStatusOpen, setConfirmStatusOpen] = React.useState(false);
  const [pendingStatusAction, setPendingStatusAction] = React.useState<{
    id: string;
    nextStatus: 'pending' | 'reviewed' | 'implemented';
  } | null>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = React.useState(false);
  const [pendingDeleteId, setPendingDeleteId] = React.useState<string | null>(null);
  const [feedbackSortField, setFeedbackSortField] = React.useState<SortableServerField>('created_at');
  const [feedbackSortDirection, setFeedbackSortDirection] = React.useState<SortDir>('desc');
  
  // Pagination states
  const [feedbackCurrentPage, setFeedbackCurrentPage] = React.useState(1);
  const [feedbackItemsPerPage, setFeedbackItemsPerPage] = React.useState(10);
  // Bumped after a delete to force the (abort-safe) feedback effect to refetch
  // the current page from the server, so a non-last-page delete refills the row
  // that slides up instead of leaving the page short.
  const [feedbackRefetchTick, setFeedbackRefetchTick] = React.useState(0);

  const userById = React.useMemo(() => {
    const map: Record<number, any> = {};
    (users || []).forEach((u: any) => { map[u.id] = u; });
    return map;
  }, [users]);

  const feedbackTotalPages = Math.max(1, Math.ceil(feedbackTotal / feedbackItemsPerPage));

  // Snap to last valid page when total shrinks below current page.
  // Fires after deleteFeedback decrements feedbackTotal — without this, deleting
  // the last item on the last page strands the admin on an empty view.
  useEffect(() => {
    if (feedbackCurrentPage > feedbackTotalPages) {
      setFeedbackCurrentPage(feedbackTotalPages);
    }
  }, [feedbackTotalPages, feedbackCurrentPage]);

  // Initial fetch (users + analytics) — fires once on mount
  useEffect(() => {
    dispatch(fetchAllUsers());
    dispatch(fetchAnalytics());
  }, [dispatch]);

  // Feedback fetch — re-runs whenever page, page size, sort field or sort dir change.
  // Server returns the page (already sorted) + X-Total-Count for pagination UI.
  // The cleanup aborts the previous in-flight request so a rapid sort→page→sort
  // sequence can't have an older response overwrite a newer one out of order.
  useEffect(() => {
    const promise = dispatch(fetchAllFeedback({
      skip: (feedbackCurrentPage - 1) * feedbackItemsPerPage,
      limit: feedbackItemsPerPage,
      sortField: feedbackSortField,
      sortDir: feedbackSortDirection,
    }));
    return () => { promise.abort(); };
  }, [dispatch, feedbackCurrentPage, feedbackItemsPerPage, feedbackSortField, feedbackSortDirection, feedbackRefetchTick]);

  const handleFeedbackSort = (field: string) => {
    if (!SORTABLE_SERVER_FIELDS.includes(field as SortableServerField)) return;
    const next = field as SortableServerField;
    if (feedbackSortField === next) {
      setFeedbackSortDirection(feedbackSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setFeedbackSortField(next);
      setFeedbackSortDirection('desc');
    }
    setFeedbackCurrentPage(1);
  };

  const getFeedbackSortIcon = (field: string) => {
    if (feedbackSortField !== field) return null;
    return feedbackSortDirection === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />;
  };

  const getFeedbackStatusLabel = (status?: string) => {
    const normalized = (status || '').toLowerCase();
    if (normalized === 'implemented' || normalized === 'resolved') return 'Implemented/Fixed';
    if (normalized === 'reviewed') return 'Reviewed';
    return 'Pending';
  };

  const getFeedbackStatusColor = (status?: string) => {
    const normalized = (status || '').toLowerCase();
    if (normalized === 'implemented' || normalized === 'resolved') return 'success';
    if (normalized === 'reviewed') return 'warning';
    return 'default';
  };

  const openDeleteDialog = (id: string) => {
    setPendingDeleteId(id);
    setConfirmDeleteOpen(true);
  };

  const closeDeleteDialog = () => {
    setConfirmDeleteOpen(false);
    setPendingDeleteId(null);
  };

  // Mock data for charts (replace with real data from analytics)
  const activeUsersData = [
    { name: 'Mon', daily: 45, weekly: 120, monthly: 450 },
    { name: 'Tue', daily: 52, weekly: 135, monthly: 480 },
    { name: 'Wed', daily: 48, weekly: 128, monthly: 460 },
    { name: 'Thu', daily: 61, weekly: 142, monthly: 520 },
    { name: 'Fri', daily: 55, weekly: 138, monthly: 490 },
    { name: 'Sat', daily: 38, weekly: 95, monthly: 380 },
    { name: 'Sun', daily: 42, weekly: 108, monthly: 420 },
  ];

  const qaUsageData = [
    { name: 'QA Usage', value: 65, color: '#a31830' },
    { name: 'Digest Only', value: 35, color: '#1976d2' },
  ];

  const topQueriesData = [
    { query: 'Meeting schedule', count: 45, similarity: 0.92 },
    { query: 'Project updates', count: 38, similarity: 0.88 },
    { query: 'Deadline information', count: 32, similarity: 0.85 },
    { query: 'Team collaboration', count: 28, similarity: 0.82 },
    { query: 'Client feedback', count: 25, similarity: 0.79 },
  ];



  return (
      <Fade in timeout={600}>
        <Container maxWidth="xl" sx={{ mt: isMobile ? 2 : 4, mb: isMobile ? 2 : 4, px: isMobile ? 1 : 3 }}>
        <Typography 
          variant={isMobile ? "h5" : "h4"} 
          component="h1" 
          gutterBottom 
          sx={{ 
            fontWeight: 500, 
            mb: isMobile ? 2 : 4,
            fontSize: isMobile ? '1.5rem' : undefined
          }}
        >
          Admin Dashboard
        </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Key Metrics */}
      <Grid container spacing={isMobile ? 2 : 3} sx={{ mb: isMobile ? 3 : 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Fade in timeout={600}>
            <Card>
            <CardContent>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: isMobile ? 1 : 2,
                flexDirection: isMobile ? 'column' : 'row',
                textAlign: isMobile ? 'center' : 'left'
              }}>
                <Avatar sx={{ 
                  bgcolor: 'primary.main', 
                  mr: isMobile ? 0 : 2,
                  mb: isMobile ? 1 : 0,
                  width: isMobile ? 40 : 56,
                  height: isMobile ? 40 : 56
                }}>
                  <People fontSize={isMobile ? 'medium' : 'large'} />
                </Avatar>
                <Box>
                  <Typography 
                    variant={isMobile ? "h5" : "h4"} 
                    component="div" 
                    sx={{ 
                      fontWeight: 500,
                      fontSize: isMobile ? '1.5rem' : undefined
                    }}
                  >
                    {users.length}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{ fontSize: isMobile ? '0.75rem' : undefined }}
                  >
                    Total Users
                  </Typography>
                </Box>
              </Box>
            </CardContent>
            </Card>
          </Fade>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Fade in timeout={600}>
            <Card>
            <CardContent>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: isMobile ? 1 : 2,
                flexDirection: isMobile ? 'column' : 'row',
                textAlign: isMobile ? 'center' : 'left'
              }}>
                <Avatar sx={{ 
                  bgcolor: 'success.main', 
                  mr: isMobile ? 0 : 2,
                  mb: isMobile ? 1 : 0,
                  width: isMobile ? 40 : 56,
                  height: isMobile ? 40 : 56
                }}>
                  <TrendingUp fontSize={isMobile ? 'medium' : 'large'} />
                </Avatar>
                <Box>
                  <Typography 
                    variant={isMobile ? "h5" : "h4"} 
                    component="div" 
                    sx={{ 
                      fontWeight: 500,
                      fontSize: isMobile ? '1.5rem' : undefined
                    }}
                  >
                    {analytics?.scraping_success_rate?.static || 0}%
                  </Typography>
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{ fontSize: isMobile ? '0.75rem' : undefined }}
                  >
                    Success Rate
                  </Typography>
                </Box>
              </Box>
            </CardContent>
            </Card>
          </Fade>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Fade in timeout={800}>
            <Card>
            <CardContent>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: isMobile ? 1 : 2,
                flexDirection: isMobile ? 'column' : 'row',
                textAlign: isMobile ? 'center' : 'left'
              }}>
                <Avatar sx={{ 
                  bgcolor: 'info.main', 
                  mr: isMobile ? 0 : 2,
                  mb: isMobile ? 1 : 0,
                  width: isMobile ? 40 : 56,
                  height: isMobile ? 40 : 56
                }}>
                  <Speed fontSize={isMobile ? 'medium' : 'large'} />
                </Avatar>
                <Box>
                  <Typography 
                    variant={isMobile ? "h5" : "h4"} 
                    component="div" 
                    sx={{ 
                      fontWeight: 500,
                      fontSize: isMobile ? '1.5rem' : undefined
                    }}
                  >
                    {analytics?.pipeline_latency?.average?.toFixed(1) || 0}s
                  </Typography>
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{ fontSize: isMobile ? '0.75rem' : undefined }}
                  >
                    Avg Latency
                  </Typography>
                </Box>
              </Box>
            </CardContent>
            </Card>
          </Fade>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Fade in timeout={1000}>
            <Card>
            <CardContent>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: isMobile ? 1 : 2,
                flexDirection: isMobile ? 'column' : 'row',
                textAlign: isMobile ? 'center' : 'left'
              }}>
                <Avatar sx={{ 
                  bgcolor: 'warning.main', 
                  mr: isMobile ? 0 : 2,
                  mb: isMobile ? 1 : 0,
                  width: isMobile ? 40 : 56,
                  height: isMobile ? 40 : 56
                }}>
                  <Error fontSize={isMobile ? 'medium' : 'large'} />
                </Avatar>
                <Box>
                  <Typography 
                    variant={isMobile ? "h5" : "h4"} 
                    component="div" 
                    sx={{ 
                      fontWeight: 500,
                      fontSize: isMobile ? '1.5rem' : undefined
                    }}
                  >
                    {analytics?.job_failures?.total || 0}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{ fontSize: isMobile ? '0.75rem' : undefined }}
                  >
                    Job Failures
                  </Typography>
                </Box>
              </Box>
            </CardContent>
            </Card>
          </Fade>
        </Grid>
      </Grid>

      {/* Analytics Charts */}
      <Grid container spacing={isMobile ? 2 : 3} sx={{ mb: isMobile ? 3 : 4 }}>
        <Grid item xs={12} lg={8}>
          <ErrorBoundary>
            <Fade in timeout={600}>
            <Card>
            <CardContent>
              <Typography 
                variant="h6" 
                gutterBottom 
                sx={{ 
                  fontWeight: 500, 
                  mb: isMobile ? 2 : 3,
                  fontSize: isMobile ? '1rem' : undefined
                }}
              >
                Active Users Trend
              </Typography>
              <ResponsiveContainer width="100%" height={isMobile ? 250 : 300}>
                <LineChart data={activeUsersData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <RechartsTooltip />
                  <Line type="monotone" dataKey="daily" stroke="#a31830" strokeWidth={2} />
                  <Line type="monotone" dataKey="weekly" stroke="#1976d2" strokeWidth={2} />
                  <Line type="monotone" dataKey="monthly" stroke="#388e3c" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
            </Card>
          </Fade>
          </ErrorBoundary>
        </Grid>

        <Grid item xs={12} lg={4}>
          <ErrorBoundary>
            <Fade in timeout={800}>
            <Card>
            <CardContent>
              <Typography 
                variant="h6" 
                gutterBottom 
                sx={{ 
                  fontWeight: 500, 
                  mb: isMobile ? 2 : 3,
                  fontSize: isMobile ? '1rem' : undefined
                }}
              >
                QA vs Digest Usage
              </Typography>
              <ResponsiveContainer width="100%" height={isMobile ? 250 : 300}>
                <RechartsPieChart>
                  <Pie
                    data={qaUsageData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {qaUsageData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <RechartsTooltip />
                </RechartsPieChart>
              </ResponsiveContainer>
            </CardContent>
            </Card>
          </Fade>
          </ErrorBoundary>
        </Grid>
      </Grid>

      {/* Top Queries */}
      <ErrorBoundary>
        <Fade in timeout={800}>
        <Card sx={{ mb: isMobile ? 3 : 4 }}>
        <CardContent>
          <Typography 
            variant="h6" 
            gutterBottom 
            sx={{ 
              fontWeight: 500, 
              mb: isMobile ? 2 : 3,
              fontSize: isMobile ? '1rem' : undefined
            }}
          >
            Top Queries (with Similarity Scores)
          </Typography>
          <ResponsiveContainer width="100%" height={isMobile ? 250 : 300}>
            <RechartsBarChart data={topQueriesData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="query" angle={-45} textAnchor="end" height={80} />
              <YAxis />
              <RechartsTooltip />
              <Bar dataKey="count" fill="#a31830" />
            </RechartsBarChart>
          </ResponsiveContainer>
        </CardContent>
        </Card>
      </Fade>
      </ErrorBoundary>

      {/* Chat Sessions Monitor */}
        <Box sx={{ mt: isMobile ? 3 : 4 }}>
          <ChatMonitor users={users} />
        </Box>

      {/* Message Feedback Monitor */}
        <Box sx={{ mt: isMobile ? 3 : 4 }}>
          <FeedbackMonitor users={users} />
        </Box>

      {/* Feedback Management */}
      <Fade in timeout={1200}>
        <Card sx={{ mt: isMobile ? 3 : 4 }}>
        <CardContent>
          <Typography 
            variant="h6" 
            gutterBottom 
            sx={{ 
              fontWeight: 500, 
              mb: isMobile ? 2 : 3,
              fontSize: isMobile ? '1rem' : undefined
            }}
          >
            All Feedback
          </Typography>

          {loading ? (
            <LinearProgress />
          ) : (
            <>
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell 
                      onClick={() => handleFeedbackSort('category')} 
                      sx={{ cursor: 'pointer', userSelect: 'none' }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        Category
                        {getFeedbackSortIcon('category')}
                      </Box>
                    </TableCell>
                    <TableCell 
                      onClick={() => handleFeedbackSort('rating')} 
                      sx={{ cursor: 'pointer', userSelect: 'none' }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        Rating
                        {getFeedbackSortIcon('rating')}
                      </Box>
                    </TableCell>
                    <TableCell 
                      onClick={() => handleFeedbackSort('comment')} 
                      sx={{ cursor: 'pointer', userSelect: 'none' }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        Comment
                        {getFeedbackSortIcon('comment')}
                      </Box>
                    </TableCell>
                    <TableCell 
                      onClick={() => handleFeedbackSort('status')} 
                      sx={{ cursor: 'pointer', userSelect: 'none' }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        Status
                        {getFeedbackSortIcon('status')}
                      </Box>
                    </TableCell>
                    <TableCell
                      onClick={() => handleFeedbackSort('created_at')}
                      sx={{ cursor: 'pointer', userSelect: 'none' }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        Submitted
                        {getFeedbackSortIcon('created_at')}
                      </Box>
                    </TableCell>
                    <TableCell>GitHub</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {feedback.map((item: any) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ width: 24, height: 24, bgcolor: userById[item.user_id]?.role === 'admin' ? 'primary.main' : 'grey.500' }}>
                            {(userById[item.user_id]?.username || '?').charAt(0).toUpperCase()}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 400 }}>
                              {userById[item.user_id]?.username || `User #${item.user_id}`}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {userById[item.user_id]?.email || ''}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={item.category}
                          color={item.category === 'bug' ? 'error' : item.category === 'feature' ? 'info' : item.category === 'general' ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{item.rating}</TableCell>
                      <TableCell>
                        <Box sx={{ maxWidth: 360 }}>
                          <Typography
                            variant="body2"
                            title={item.comment}
                            sx={{
                              whiteSpace: 'normal',
                              wordBreak: 'break-word',
                              overflowWrap: 'anywhere',
                            }}
                          >
                            {item.comment}
                          </Typography>

                          {Array.isArray(item.attachments) && item.attachments.length > 0 && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                              {item.attachments.map((a: any) => {
                                const url = `${backendUrl}${a.url}`;
                                return (
                                  <Box key={a.filename} sx={{ display: 'inline-flex' }}>
                                    <FeedbackAttachmentThumb
                                      apiUrl={url}
                                      filename={a.filename}
                                      token={token}
                                      size={44}
                                    />
                                  </Box>
                                );
                              })}
                            </Box>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getFeedbackStatusLabel(item.status)}
                          color={getFeedbackStatusColor(item.status) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{safeFormatDate(item.created_at, 'MMM dd, yyyy HH:mm', '')}</TableCell>
                      <TableCell>
                        {item.github_issue_url ? (
                          <Link
                            href={item.github_issue_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            underline="hover"
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.5,
                              fontSize: '0.8rem',
                            }}
                          >
                            <GitHubIcon sx={{ fontSize: '1rem' }} />
                            #{item.github_issue_number}
                          </Link>
                        ) : (
                          <Typography variant="caption" color="text.disabled">--</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                          {(() => {
                            const status = (item.status || 'pending').toLowerCase();
                            if (status === 'pending') {
                              return (
                            <>
                              <Button
                                size="small"
                                variant="contained"
                                color="success"
                                onClick={() => {
                                  setPendingStatusAction({ id: item.id, nextStatus: 'reviewed' });
                                  setConfirmStatusOpen(true);
                                }}
                              >
                                Mark Reviewed
                              </Button>
                              <Button
                                size="small"
                                variant="contained"
                                color="primary"
                                onClick={() => {
                                  setPendingStatusAction({ id: item.id, nextStatus: 'implemented' });
                                  setConfirmStatusOpen(true);
                                }}
                              >
                                Mark Implemented
                              </Button>
                            </>
                              );
                            }

                            if (status === 'reviewed') {
                              return (
                            <>
                              <Button
                                size="small"
                                variant="outlined"
                                color="warning"
                                onClick={() => {
                                  setPendingStatusAction({ id: item.id, nextStatus: 'pending' });
                                  setConfirmStatusOpen(true);
                                }}
                              >
                                Mark Pending
                              </Button>
                              <Button
                                size="small"
                                variant="contained"
                                color="primary"
                                onClick={() => {
                                  setPendingStatusAction({ id: item.id, nextStatus: 'implemented' });
                                  setConfirmStatusOpen(true);
                                }}
                              >
                                Mark Implemented
                              </Button>
                            </>
                              );
                            }

                            if (status === 'implemented' || status === 'resolved') {
                              return (
                            <>
                              <Button
                                size="small"
                                variant="outlined"
                                color="warning"
                                onClick={() => {
                                  setPendingStatusAction({ id: item.id, nextStatus: 'pending' });
                                  setConfirmStatusOpen(true);
                                }}
                              >
                                Mark Pending
                              </Button>
                              <Button
                                size="small"
                                variant="contained"
                                color="success"
                                onClick={() => {
                                  setPendingStatusAction({ id: item.id, nextStatus: 'reviewed' });
                                  setConfirmStatusOpen(true);
                                }}
                              >
                                Mark Reviewed
                              </Button>
                            </>
                              );
                            }

                            return null;
                          })()}

                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            startIcon={<DeleteOutlined />}
                            onClick={() => openDeleteDialog(item.id)}
                          >
                            Delete
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {feedbackTotal > 0 && (
              <Pagination
                currentPage={feedbackCurrentPage}
                totalPages={feedbackTotalPages}
                totalItems={feedbackTotal}
                itemsPerPage={feedbackItemsPerPage}
                onPageChange={setFeedbackCurrentPage}
                onItemsPerPageChange={setFeedbackItemsPerPage}
              />
            )}
            </>
          )}
        </CardContent>
        </Card>
      </Fade>

      {/* Confirm Feedback Status Change */}
      <Dialog open={confirmStatusOpen} onClose={() => setConfirmStatusOpen(false)}>
        <DialogTitle>Confirm Status Change</DialogTitle>
        <DialogContent>
          <Typography>
            {pendingStatusAction
              ? `Are you sure you want to mark this feedback as ${getFeedbackStatusLabel(pendingStatusAction.nextStatus)}?`
              : ''}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmStatusOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              if (pendingStatusAction) {
                dispatch(updateFeedbackStatus({ feedbackId: pendingStatusAction.id, status: pendingStatusAction.nextStatus }));
              }
              setConfirmStatusOpen(false);
              setPendingStatusAction(null);
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Feedback Delete */}
      <Dialog open={confirmDeleteOpen} onClose={closeDeleteDialog}>
        <DialogTitle>Delete Feedback</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this feedback? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              if (pendingDeleteId) {
                dispatch(deleteFeedback({ feedbackId: pendingDeleteId }))
                  .unwrap()
                  .then(() => setFeedbackRefetchTick((t) => t + 1))
                  .catch(() => {});
              }
              closeDeleteDialog();
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
        </Container>
      </Fade>
  );
};

export default AdminDashboard;
